import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import { useRouter } from 'next/router';
import {
  FC, useCallback, useEffect, useMemo, useState,
} from 'react';
import LazyLightbox from 'src/common/components/Lightbox/LazyLightbox';
import { useMediaQueries } from 'src/common/providers/MediaQueryProvider';
import usePhotoLightbox from 'src/common/hooks/usePhotoLightbox';
import type { Photo } from 'src/common/types/api/v4/boats/search';
import { GALLERY_VIEW } from 'src/listing/constants';
import trackListingPhotoClick from 'src/common/utils/trackListingPhotoClick';
import BackButton from '../BackButton';
import ImgFromSrcSet, { ImgFromSrcSetProps } from '../ImgFromSrcSet';
import styles from './PhotoGallery.module.scss';

const ONE_COLUMN_WIDTH = 440; // Max width for mobile devices
const TWO_COLUMNS_WIDTH = 488; // Is the rounded width for 2 columns
const THREE_COLUMNS_WIDTH = 380; // Is the rounded width for 3 columns
const IMAGE_LOAD_WAVE_DELAY_SECONDS = 0.1;
const PLACEHOLDER_DELAY_MS = 300;

type PhotoGalleryProps = {
  photos?: Photo[];
  headline: string;
  listingId: string;
};

// Calculate responsive srcset based on intrinsic image dimensions
const calculateResponsiveSrcSet = (
  intrinsicWidth: number,
  intrinsicHeight: number,
): ImgFromSrcSetProps['srcSet'] => {
  const aspectRatio = intrinsicWidth / intrinsicHeight;
  const maxWidth = intrinsicWidth;

  // Clamp all requested widths to the intrinsic width to avoid upscaling
  const widthForThreeColumns = Math.min(THREE_COLUMNS_WIDTH, maxWidth);
  const heightForThreeColumns = Math.round(widthForThreeColumns / aspectRatio);
  const widthForTwoColumns = Math.min(TWO_COLUMNS_WIDTH, maxWidth);
  const heightForTwoColumns = Math.round(widthForTwoColumns / aspectRatio);

  // Mobile/default width is additionally capped by MAX_MOBILE_WIDTH
  const mobileWidth = Math.min(maxWidth, ONE_COLUMN_WIDTH);
  const mobileHeight = Math.round(mobileWidth / aspectRatio);

  // Responsive breakpoints with computed heights to maintain aspect ratio
  // The last entry (minWidth: 0) will be used for the default img width/height attributes
  return [
    [1024, widthForThreeColumns, heightForThreeColumns], // 3 columns
    [768, widthForTwoColumns, heightForTwoColumns], // 2 columns
    // Use mobileWidth for mobile/default to avoid very large images or upscaling
    [0, mobileWidth, mobileHeight],
  ];
};

// Returns the column index for animation delay: 0 = first column, 1 = second, 2 = third.
// Images are distributed across columns so each column has ~ totalImages/columnCount images.
const getVisualColumnIndex = (index: number, columnCount: number, totalImages: number): number => {
  if (columnCount <= 1) return 0;
  const imagesPerColumn = Math.ceil(totalImages / columnCount);
  return Math.min(Math.floor(index / imagesPerColumn), columnCount - 1);
};

const PhotoGallery: FC<PhotoGalleryProps> = ({
  photos = [],
  headline,
  listingId,
}) => {
  const [t] = useTranslation(['listing-page', 'common']);
  const router = useRouter();
  const { m, l } = useMediaQueries();
  const [showImages, setShowImages] = useState(false);

  const columnCount = 1 + Number(m) + Number(l);

  // Filter photos with valid dimensions to ensure consistent indexing
  const photosWithDimensions = useMemo(
    () => photos.filter(({ width, height }) => width && height),
    [photos],
  );

  // Precompute srcSets once per photo set to avoid recalculating on every render
  const srcSetsByIndex = useMemo(
    () => photosWithDimensions.map(({ width, height }) => calculateResponsiveSrcSet(width, height)),
    [photosWithDimensions],
  );

  // Precompute animation delays for placeholders
  const animationDelays = useMemo(() => {
    const total = photosWithDimensions.length;
    return Array.from(
      { length: total },
      (_, index) => `${getVisualColumnIndex(index, columnCount, total) * IMAGE_LOAD_WAVE_DELAY_SECONDS}s`,
    );
  }, [photosWithDimensions.length, columnCount]);

  const {
    lightboxOpen,
    lightboxStartIndex,
    lightboxImages,
    openLightbox,
    closeLightbox,
  } = usePhotoLightbox({
    photos: photosWithDimensions,
    headline,
  });

  useEffect(() => {
    // If the user has opted in to reduced motion, show the images immediately
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      setShowImages(true);
      return () => {};
    }

    // Otherwise, show the placeholder images with a delay
    const timeoutId = setTimeout(() => setShowImages(true), PLACEHOLDER_DELAY_MS);
    return () => clearTimeout(timeoutId);
  }, []);

  const handleBackButtonClick = useCallback(() => {
    router.replace(router.asPath.split('#')[0]);
  }, [router]);

  const onImageClick = useCallback((index: number) => {
    trackListingPhotoClick({
      index,
      imageLocation: 'gallery',
      listingId,
      totalPhotos: photosWithDimensions.length,
    });
    // Pass event parameter as undefined to avoid preventing default in openLightbox
    openLightbox(undefined, index);
  }, [listingId, openLightbox, photosWithDimensions.length]);

  return (
    <div className={styles.root}>
      <div className={styles.backButtonContainer}>
        <BackButton
          label={t('common:back-button-label')}
          onClick={handleBackButtonClick}
          position="static"
        />
      </div>

      {photosWithDimensions.length > 0 && (
        <ul className={styles.list}>
          {photosWithDimensions.map(({
            description, url, width, height,
          }, index) => (
            <li key={url}>
              {!showImages ? (
                <div
                  className={styles.placeholder}
                  style={{
                    aspectRatio: `${width} / ${height}`,
                    animationDelay: animationDelays[index],
                  }}
                  aria-hidden
                  id={`${GALLERY_VIEW}/${index}`}
                />
              ) : (
                <Link
                  id={`${GALLERY_VIEW}/${index}`}
                  href={`#${GALLERY_VIEW}/${index}`}
                  className={styles.link}
                  onClick={() => onImageClick(index)}
                  replace
                  scroll={false}
                  shallow
                >
                  <ImgFromSrcSet
                    withRoundedCornersLg={false}
                    withPlaceholder
                    withFixedLayout
                    src={url}
                    alt={description || headline}
                    srcSet={srcSetsByIndex[index]}
                    loading="lazy"
                  />
                </Link>
              )}
            </li>
          ))}
        </ul>
      )}

      <LazyLightbox
        label={headline}
        images={lightboxImages}
        isOpen={lightboxOpen}
        startIndex={lightboxStartIndex}
        closeModal={closeLightbox}
        enableSideClickNavigation
      />
    </div>
  );
};

export default PhotoGallery;
